import { useGetMyOrganizationMembershipsQuery } from '@codezo/graphql';
import { Check, ChevronsUpDown } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Logo } from './ui/logo';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { useSwitchOrganization } from '../hooks/useOrganizationSwitch';

function OrgMark({ name, logo }: { name: string; logo?: string | null }) {
	// Logos are time-limited signed URLs; an expired one falls back to the initial.
	const [failed, setFailed] = useState(false);
	if (logo && !failed) {
		return (
			<img
				src={logo}
				alt=""
				onError={() => setFailed(true)}
				className="size-5 shrink-0 rounded-sm border border-line-1 bg-surface-fill object-contain"
			/>
		);
	}
	return (
		<span className="flex size-5 shrink-0 items-center justify-center rounded-sm border border-line-1 bg-surface-fill font-mono text-2xs font-medium uppercase text-text-2">
			{name.charAt(0)}
		</span>
	);
}

function BrandLabel({ orgName }: { orgName: string }) {
	return (
		<div className="flex min-w-0 flex-col leading-tight">
			<span className="truncate text-base font-medium -tracking-[0.01em]">{orgName}</span>
			<span className="text-xs tracking-[0.04em] text-text-3">Platform</span>
		</div>
	);
}

/**
 * The sidebar's brand block. For a user in more than one organization it
 * opens a menu to move the session to another one; with a single membership
 * (or a guest session, which only ever sees one) it stays a plain label.
 */
export function OrganizationSwitcher({
	orgName,
	currentOrganizationId,
	returnPath,
}: {
	orgName: string;
	currentOrganizationId: string | undefined;
	/** The section to reopen in the new organization, e.g. `/quotes`. */
	returnPath: string;
}) {
	const { data, refetch } = useGetMyOrganizationMembershipsQuery({ fetchPolicy: 'no-cache' });
	const switchTo = useSwitchOrganization();
	const [switchingTo, setSwitchingTo] = useState<string | null>(null);

	const memberships = data?.getMyOrganizationMemberships ?? [];

	if (memberships.length <= 1) {
		return (
			<div className="flex items-center gap-2.5 px-4 pt-4 pb-4 text-text-1">
				<Logo />
				<BrandLabel orgName={orgName} />
			</div>
		);
	}

	const handleSwitch = async (organizationId: string) => {
		if (organizationId === currentOrganizationId || switchingTo) return;
		setSwitchingTo(organizationId);
		try {
			await switchTo(organizationId, returnPath);
		} catch (err) {
			setSwitchingTo(null);
			// Typically FORBIDDEN_PERMISSIONS: removed from that organization
			// meanwhile. An expired session is handled by the global error link.
			toast.error(err instanceof Error ? err.message : 'Could not switch organization');
			void refetch();
		}
	};

	return (
		<div className="px-2 pt-2 pb-2">
			<DropdownMenu>
				<DropdownMenuTrigger className="flex w-full cursor-pointer items-center gap-2.5 rounded-md px-2 py-2 text-left text-text-1 transition-colors hover:bg-nav-hover focus-visible:outline-none data-[popup-open]:bg-nav-hover">
					<Logo />
					<div className="min-w-0 flex-1">
						<BrandLabel orgName={orgName} />
					</div>
					<ChevronsUpDown size={14} className="shrink-0 text-text-4" />
				</DropdownMenuTrigger>
				<DropdownMenuContent side="bottom" align="start" className="w-[220px]">
					<div className="px-2 pt-1 pb-1.5 text-2xs font-medium uppercase tracking-[0.12em] text-text-4">
						Organizations
					</div>
					{memberships.map(({ id, organizationId, organization }) => {
						const name = organization?.name ?? 'Organization';
						const isCurrent = organizationId === currentOrganizationId;
						return (
							<DropdownMenuItem
								key={id}
								disabled={!!switchingTo}
								onClick={() => handleSwitch(organizationId)}
							>
								<OrgMark name={name} logo={organization?.logo} />
								<span className="min-w-0 flex-1 truncate">
									{switchingTo === organizationId
										? `Switching to ${name}…`
										: name}
								</span>
								{isCurrent && <Check size={14} className="text-text-2" />}
							</DropdownMenuItem>
						);
					})}
				</DropdownMenuContent>
			</DropdownMenu>
		</div>
	);
}
